import { getUpdater } from '@advanza/advanza_generic'
import { AnyAction, createReducer } from '@reduxjs/toolkit'
import { Municipality as BaseMunicipality } from '@trustoo/types/entity/municipality'
import { Option } from '@trustoo/types/entity/option'
import { Question } from '@trustoo/types/entity/question'
import { QuestionRule } from '@trustoo/types/entity/questionRule'
import { Service } from '@trustoo/types/entity/service'
import { Subscription } from 'interfaces/Subscriptions'
import {
    CHANGE_SUBSCRIPTION_TRAVEL_RANGE,
    RECEIVE_ALL_GEOIDS,
    REPLACE_SUBSCRIPTION_GEOIDS,
    REPLACE_SUBSCRIPTION_MUNICIPALITIES,
    REPLACE_SUBSCRIPTION_OPTIONS,
    SUBSCRIPTION_DELETED,
    SUBSCRIPTION_STATUS_UPDATED,
    SUBSCRIPTIONS_CHANGE_FILTER,
    SUBSCRIPTIONS_RECEIVE_LIST,
    SUBSCRIPTIONS_RECEIVE_SINGLE,
    SUBSCRIPTIONS_REQUEST,
} from '../actions/subscriptions'
import { changeEntity, changeFilter, invalidateFilters, receiveList } from './sharedReducerHandlers'

const update = getUpdater()

type Municipality = Pick<
    BaseMunicipality,
    | 'municipality_id'
    | 'population'
    | 'name'
    | 'latitude'
    | 'longitude'
    | 'map_id'
    | 'country_code'
    | 'region_id'
> & {
    regionName: string
}

interface SubscriptionsState {
    isFetching: boolean
    didInvalidate: boolean
    entities: {
        subscriptions: Record<number | string, Subscription>
        services: Record<number, Service & { questions: number[]; highMunicipalityIds: number[] }>
        questions: Record<
            number,
            Question & {
                question_rules: QuestionRule[]
                options: number[]
            }
        >
        options: Record<
            number,
            Option & {
                subscriptions_options_id?: number
                subscription_id?: number
            }
        >
    }
    estimations: Record<number, unknown>
    geoIds: {
        isFetching?: boolean
        entities: Record<number, Municipality>
    }
    municipalities: {
        isFetching?: boolean
        entities: Record<number, Municipality>
        result?: Record<string, Record<string, number[]>>
        mapCenter?: {
            latitude: number
            longitude: number
        }
    }
    result: Record<string, Record<number, number[]>>
    counts: Record<string, number>
    filters: Record<string, any>
}

const initialState: SubscriptionsState = {
    isFetching: false,
    didInvalidate: false,
    entities: {
        options: {},
        questions: {},
        subscriptions: {},
        services: {},
    },
    estimations: {},

    geoIds: {
        entities: {},
    },
    municipalities: {
        entities: {},
    },
    result: {},
    counts: {},
    filters: {},
}

export default createReducer(initialState, (builder) => {
    builder
        .addCase(SUBSCRIPTIONS_RECEIVE_LIST, receiveList)
        .addCase(SUBSCRIPTIONS_CHANGE_FILTER, changeFilter)
        .addCase('INVALIDATE_SUBSCRIPTIONS', invalidateFilters)
        .addCase('CHANGE_ENTITY_SUBSCRIPTIONS', changeEntity)
        .addCase(SUBSCRIPTIONS_RECEIVE_SINGLE, (state, action: AnyAction) =>
            update(state, {
                isFetching: { $set: false },
                entities: { $deepMerge: action.entities },
                estimations: { $deepMerge: action.estimations || {} },
            }),
        )
        .addCase(SUBSCRIPTIONS_REQUEST, (state) =>
            update(state, {
                isFetching: { $set: true },
            }),
        )
        .addCase('REQUEST_MUNICIPALITIES', (state) =>
            update(state, {
                municipalities: {
                    isFetching: { $set: true },
                },
            }),
        )
        .addCase(RECEIVE_ALL_GEOIDS, (state, action: AnyAction) =>
            update(state, {
                geoIds: {
                    isFetching: { $set: false },
                    entities: { $set: action.entities },
                },
                municipalities: {
                    isFetching: { $set: false },
                    result: { $set: action.items },
                    entities: { $set: action.entities },
                },
            }),
        )
        .addCase(CHANGE_SUBSCRIPTION_TRAVEL_RANGE, (state, action: AnyAction) =>
            update(state, {
                entities: {
                    subscriptions: {
                        [action.subscriptionId]: {
                            postal_codes: { $set: [action.postalCode] },
                        },
                    },
                },
            }),
        )
        .addCase(SUBSCRIPTION_STATUS_UPDATED, (state, action: AnyAction) =>
            update(state, {
                entities: {
                    subscriptions: {
                        [action.subscriptionId]: {
                            active: { $set: action.active },
                        },
                    },
                },
            }),
        )
        .addCase(SUBSCRIPTION_DELETED, (state, action: AnyAction) =>
            update(state, {
                entities: {
                    subscriptions: {
                        [action.subscriptionId]: {
                            deleted: { $set: new Date().toISOString() },
                        },
                    },
                },
            }),
        )
        .addCase(REPLACE_SUBSCRIPTION_OPTIONS, (state, action: AnyAction) =>
            update(state, {
                entities: {
                    subscriptions: {
                        [action.subscriptionId]: {
                            subscriptions_options: { $set: action.options },
                        },
                    },
                },
            }),
        )
        .addCase(REPLACE_SUBSCRIPTION_GEOIDS, (state, action: AnyAction) =>
            update(state, {
                entities: {
                    subscriptions: {
                        [action.subscriptionId]: {
                            subscriptions_geoids: { $set: action.geoIds },
                        },
                    },
                },
            }),
        )
        .addCase(REPLACE_SUBSCRIPTION_MUNICIPALITIES, (state, action: AnyAction) =>
            update(state, {
                entities: {
                    subscriptions: {
                        [action.subscriptionId]: {
                            subscriptions_municipalities: { $set: action.municipalities },
                        },
                    },
                },
            }),
        )
        .addCase('SERVICE_AREA_SET_MAP_CENTER', (state, action: AnyAction) =>
            update(state, {
                municipalities: {
                    mapCenter: { $set: action },
                },
            }),
        )
})
